<template>
  <Slide class="orders-slide">
    <div class="orders-header">
      <div class="orders-title">
        <SlideTitle type="with-menu" :items="slideMenuItems">{{ getCurrentStatisticTypeLabel }}</SlideTitle>
        <p class="orders-description">{{ getCurrentStatisticTypeDescription }}</p>
      </div>
      <div class="orders-filters">
        <SlideFilters
          :products="productFilterOptions"
          :selected-product="{ id: getSelectedProductFilter || 'all' }"
          :partners="partnerFilterOptions"
          :partners-disable="!isMainPartner || isBankPartner"
          :selected-partners="{ id: getSelectedPartnersFilter || 'all' }"
          :period-items="periodFilterOptions"
          :period-selected-item="{ id: getSelectedPeriodMode || 'last_12_months' }"
          :period="{ range: true, mode: getSelectedPeriodMode, ...periodFilterLimits, ...calendarConfig, selectedPeriod: { startDate: getActiveFilters.period[0] || null, endDate: getActiveFilters.period[1] || null }}"
          :options="{ eventMap: periodCalendarEventMap }"
        />
      </div>
    </div>
    <section class="orders-content">
      <div class="order-metrics">
        <MetricsCardSkeleton :is-loading="!isSummaryDataReady" class="order-metrics-card">
          <MetricsCard title="Сьогодні" :value="getTodayValue" :type="selectedStatisticType" :trend="getTodayTrend" />
        </MetricsCardSkeleton>
        <MetricsCardSkeleton :is-loading="!isSummaryDataReady" class="order-metrics-card">
          <MetricsCard title="Вчора" :value="getYesterdayValue" :type="selectedStatisticType" :trend="getYesterdayTrend"/>
        </MetricsCardSkeleton>
        <MetricsCardSkeleton :is-loading="!isSummaryDataReady" class="order-metrics-card">
          <MetricsCard title="Тиждень" :value="getWeekValue" :type="selectedStatisticType" :trend="getWeekTrend" />
        </MetricsCardSkeleton>
        <MetricsCardSkeleton :is-loading="!isSummaryDataReady" class="order-metrics-card">
          <MetricsCard title="Місяць" :value="getMonthValue" :type="selectedStatisticType" :trend="getMonthTrend" />
        </MetricsCardSkeleton>
      </div>
      <div class="order-chart">
        <ColumnChartSkeleton :is-loading="!isOrdersDataReady" class="chart-skeleton">
          <Chart title="За календарний рік" :options="getCurrentChartOptions" />
          <EmptyChart v-if="isChartEmpty">
            <p class="message-title">{{ getEmptyOrdersChartMessage.title }}</p>
            <p class="message-description">{{ getEmptyOrdersChartMessage.description }}</p>
          </EmptyChart>
        </ColumnChartSkeleton>
      </div>
    </section>
  </Slide>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  ORDERS_PARTNER_FILTER_EVENTS,
  ORDERS_PERIOD_FILTER_EVENTS,
  ORDERS_PRODUCT_FILTER_EVENTS,
  ORDERS_STATS_EVENTS,
  ordersPartnerFilterOptions,
  ordersPeriodCalendarEventMap,
  ordersPeriodFilterOptions,
  ordersProductFilterOptions,
  ordersSlideMenuItems,
  ordersStatisticTypes,
  periodFilterLimits,
} from '@/features/order-statistics/config';
import { filterCalculator, ordersPartnerFilter, ordersPartnerSelfFilter } from '@/features/order-statistics/lib';
import { calendarConfig } from '@/shared/lib/calendar';
import { Chart, ColumnChartSkeleton, EmptyChart } from '@/shared/ui/chart';
import { MetricsCard, MetricsCardSkeleton } from '@/shared/ui/metrics-card';
import { POPUP_EVENTS } from '@/shared/ui/popup';
import { Slide, SlideFilters, SlideTitle } from '@/shared/ui/slider';

export default {
  data() {
    return {
      slideMenuItems: ordersSlideMenuItems,
      productFilterOptions: ordersProductFilterOptions,
      partnerFilterOptions: ordersPartnerFilterOptions,
      periodFilterOptions: ordersPeriodFilterOptions,
      periodFilterLimits,
      selectedStatisticType: ordersStatisticTypes[0].value,
      periodCalendarEventMap: ordersPeriodCalendarEventMap,
    };
  },
  created() {
    this._eventHandlers = {
      onStatsAmount: () => this.setSelectedStatisticType('amount'),
      onStatsCount: () => this.setSelectedStatisticType('count'),
      onStatsEarnings: () => this.setSelectedStatisticType('earnings'),
      onProductAll: () => this.setSelectedFilter('product', null, false),
      onProductOsago: () => this.setSelectedFilter('product', 'osago', true),
      onProductGreenCard: () => this.setSelectedFilter('product', 'green_card', true),
      onProductTourism: () => this.setSelectedFilter('product', 'tourism', true),
      onProductHome: () => this.setSelectedFilter('product', 'home', true),
      onProductExternal: () => this.setSelectedFilter('product', 'manual', true),
      onProductMiniKasko: () => this.setSelectedFilter('product', 'miniKasko', true),
      onProductPets: () => this.setSelectedFilter('product', 'pets', true),
      onPartnerAll: () => this.setSelectedFilter('partner', null, false),
      onPeriodLast12Months: () => { this.setPeriodMode(null); this.setSelectedFilter('period', [null, null], false); },
      onPeriodDay: () => this.setPeriodMode('days'),
      onPeriodWeek: () => this.setPeriodMode('weeks'),
      onPeriodMonth: () => this.setPeriodMode('months'),
      onPeriodDatesRange: (event) => this.setSelectedFilter('period', [event.startDate, event.endDate]),
    };

    this.fetchSummaryStatistics();
    this.fetchSalesStatistics();

    this.$eventBus.$on(ORDERS_STATS_EVENTS.ORDERS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$on(ORDERS_STATS_EVENTS.ORDERS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$on(ORDERS_STATS_EVENTS.ORDERS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$on(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$on(ORDERS_PARTNER_FILTER_EVENTS.ORDERS_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$on(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_LAST_12_MONTHS, this._eventHandlers.onPeriodLast12Months);
    this.$eventBus.$on(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$on(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$on(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$on(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  beforeDestroy() {
    this.$eventBus.$off(ORDERS_STATS_EVENTS.ORDERS_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$off(ORDERS_STATS_EVENTS.ORDERS_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$off(ORDERS_STATS_EVENTS.ORDERS_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$off(ORDERS_PRODUCT_FILTER_EVENTS.ORDERS_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$off(ORDERS_PARTNER_FILTER_EVENTS.ORDERS_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$off(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_LAST_12_MONTHS, this._eventHandlers.onPeriodLast12Months);
    this.$eventBus.$off(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$off(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$off(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$off(ORDERS_PERIOD_FILTER_EVENTS.ORDERS_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  mixins: [ ordersPartnerSelfFilter, ordersPartnerFilter, calendarConfig, filterCalculator ],
  components: { Slide, SlideTitle, SlideFilters, MetricsCard, MetricsCardSkeleton, Chart, ColumnChartSkeleton, EmptyChart },
  computed: {
    ...mapGetters('statisticSummaryModule', ['getStatisticValue', 'getStatisticValueTrend', 'isSummaryDataReady']),
    ...mapGetters('statisticOrdersModule', [
      'isOrdersDataReady', 'getStatisticChartOptions', 'isChartEmpty', 'getEmptyOrdersChartMessage','getActiveFilters',
    ]),
    ...mapGetters('currentUserModule', ['isMainPartner', 'isBankPartner']),
    getCurrentStatisticType() { return ordersStatisticTypes.find(type => type.value === this.selectedStatisticType); },
    getCurrentStatisticTypeLabel() { return this.getCurrentStatisticType?.label || ''; },
    getCurrentStatisticTypeDescription() { return this.getCurrentStatisticType?.description || ''; },
    getTodayValue() { return this.getStatisticValue(this.selectedStatisticType, 'today'); },
    getTodayTrend() { return this.getStatisticValueTrend(this.selectedStatisticType, 'today'); },
    getYesterdayValue() { return this.getStatisticValue(this.selectedStatisticType, 'yesterday'); },
    getYesterdayTrend() { return this.getStatisticValueTrend(this.selectedStatisticType, 'yesterday'); },
    getWeekValue() { return this.getStatisticValue(this.selectedStatisticType, 'week'); },
    getWeekTrend() { return this.getStatisticValueTrend(this.selectedStatisticType, 'week'); },
    getMonthValue() { return this.getStatisticValue(this.selectedStatisticType, 'month'); },
    getMonthTrend() { return this.getStatisticValueTrend(this.selectedStatisticType, 'month'); },
    getCurrentChartOptions() { return this.getStatisticChartOptions(this.selectedStatisticType); },
    getSelectedProductFilter() { return this.getActiveFilters?.product || null; },
    getSelectedPartnersFilter() { return this.getActiveFilters?.partner || null; },
    getSelectedPeriodMode() { return this.getActiveFilters?.periodMode || null; },
  },
  methods: {
    ...mapActions('statisticSummaryModule', ['fetchSummaryStatistics']),
    ...mapActions('statisticOrdersModule', ['fetchSalesStatistics', 'setupFilter', 'setupPeriodMode']),
    setSelectedStatisticType(type) {
      if (this.selectedStatisticType !== type) {
        this.selectedStatisticType = type;
      }

      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setPeriodMode(mode) {
      if (this.getSelectedPeriodMode !== mode) {
        this.setupPeriodMode(mode);
      }
    },
    setSelectedFilter(type, value, multiselect = false, refetchData = true) {
      this.setupFilter({
        filters: this.calculateFilterValues(this.getActiveFilters, type, value, multiselect),
        mode: 'update',
        refetchData
      });
      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
  },
  watch: {
    isOrdersDataReady: {
      immediate: false,
      handler(isReady) {
        if (isReady) {
          this.$emit('update-slider');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.orders-slide {
  min-height: 795px;
  padding: 24px 24px 65px;

  @media (max-width: 450px) {
    padding: 24px 24px 120px;
  }
}

.orders-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;

  @media (max-width: 1050px) {
    flex-direction: column;
    align-items: start;
  }
}

.orders-description {
  font-size: 14px;
  font-weight: 400;
  color: #71717A;
}

.order-metrics {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 25px;

  @media (max-width: 1050px) {
    margin-bottom: 0;
  }

  &-card {
    width: 24%;

    @media (max-width: 1050px) {
      width: 48%;
      margin-bottom: 25px;
    }
  }
}

.message {
  &-title {
    font-weight: 600;
    font-size: 20px;
    margin-bottom: 20px;
    text-align: center;
  }

  &-description {
    font-weight: 400;
    font-size: 16px;
    text-align: center;
  }
}

.chart-skeleton {
  min-height: 520px;
}

@media (max-width: 1050px) {
  .orders-title {
    margin-bottom: 16px;
  }
}
</style>
